import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  Edit,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import BusRouteApi from "Services/BusRoute";
import BusRouteCreateModal from "Pages/Modals/BusRoutes/Create";
import BusRouteDeleteModal from "Pages/Modals/BusRoutes/Delete";

const BusRoutes = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [addBusRouteModal, setAddBusRouteModal] = useState(false);
  const [deleteBusRouteModal, setDeleteBusRouteModal] = useState(false);
  const [selectedRouteId, setSelectedRouteId] = useState("");

  const normalizeBusRoutesList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.routes)) {
      return { rows: payload.routes || [], total: payload?.total || payload?.routes?.length || 0 };
    }
    if (Array.isArray(payload?.data?.routes)) {
      return { rows: payload.data.routes || [], total: payload?.data?.total || payload?.data?.routes?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getBusRoutes = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    BusRouteApi.getBusRoutesList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeBusRoutesList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllBusRoutes = useCallback(() => {
    BusRouteApi.getBusRoutesList({ page: 1, limit: 1000, search: "" }).then(
      (res) => {
        const normalized = normalizeBusRoutesList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getBusRoutes();
    getAllBusRoutes();
  }, [getBusRoutes, getAllBusRoutes]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => ({
    id: data?.id || index + 1,
    sno: calculateSno(page, pageSize, index),
    routeId: data?.id || "-",
    routeCode: data?.route_code || "-",
    origin: data?.places_bus_routes_origin_place_idToplaces?.name || data?.origin_place_id || "-",
    destination: data?.places_bus_routes_destination_place_idToplaces?.name || data?.destination_place_id || "-",
    duration: data?.duration_minutes ?? 0,
    active: data?.is_active ? "Active" : "Inactive",
    raw: data,
  }));

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Route ID": r.routeId,
    "Route Code": r.routeCode,
    Origin: r.origin,
    Destination: r.destination,
    "Duration (min)": r.duration,
    Status: r.active,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["route_code"], ["asc"]).map((data, index) => ({
    "S.NO": index + 1,
    "Route ID": data?.id || "-",
    "Route Code": data?.route_code || "-",
    Origin: data?.places_bus_routes_origin_place_idToplaces?.name || data?.origin_place_id || "-",
    Destination: data?.places_bus_routes_destination_place_idToplaces?.name || data?.destination_place_id || "-",
    "Duration (min)": data?.duration_minutes ?? 0,
    Status: data?.is_active ? "Active" : "Inactive",
  }));

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleAdd = () => {
    setSelectedRouteId("");
    setAddBusRouteModal(true);
  };
  const handleCloseAdd = (created = false) => {
    setAddBusRouteModal(false);
    setSelectedRouteId("");
    if (created) {
      getBusRoutes();
      getAllBusRoutes();
    }
  };
  const handleEdit = (routeRow) => {
    const routeId = routeRow?.id || routeRow?._id;
    if (!routeId) {
      dispatch(
        showSnackbar({
          message: "Route ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedRouteId(routeId);
    setAddBusRouteModal(true);
  };
  const handleDelete = (routeRow) => {
    const routeId = routeRow?.id || routeRow?._id;
    if (!routeId) {
      dispatch(
        showSnackbar({
          message: "Route ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedRouteId(routeId);
    setDeleteBusRouteModal(true);
  };
  const handleCloseDelete = (deleted = false) => {
    setDeleteBusRouteModal(false);
    setSelectedRouteId("");
    if (deleted) {
      getBusRoutes();
      getAllBusRoutes();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BusRoutes");
    XLSX.writeFile(workbook, "BusRoutes.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "BusRoutes.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.autoTable({
      head: [["S.NO", "Route ID", "Route Code", "Origin", "Destination", "Duration (min)", "Status"]],
      body: formatted.map((r) => Object.values(r)),
      styles: { fontSize: 8 },
    });
    doc.save("BusRoutes.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All BusRoutes.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeStyle = (value) => {
    const key = String(value || "").toLowerCase();
    const map = {
      approved: { bg: "#14b8a6", fg: "#ffffff" },
      active: { bg: "#10b981", fg: "#ffffff" },
      published: { bg: "#10b981", fg: "#ffffff" },
      confirmed: { bg: "#10b981", fg: "#ffffff" },
      completed: { bg: "#0ea5e9", fg: "#ffffff" },
      onboarding: { bg: "#ef4444", fg: "#ffffff" },
      boarding: { bg: "#ef4444", fg: "#ffffff" },
      cancelled: { bg: "#ef4444", fg: "#ffffff" },
      rejected: { bg: "#ef4444", fg: "#ffffff" },
      pending: { bg: "#f59e0b", fg: "#111827" },
      draft: { bg: "#6b7280", fg: "#ffffff" },
      full: { bg: "#7c3aed", fg: "#ffffff" },
      departed: { bg: "#2563eb", fg: "#ffffff" },
      arrived: { bg: "#14b8a6", fg: "#ffffff" },
      inactive: { bg: "#9ca3af", fg: "#111827" },
    };
    return map[key] || { bg: "#64748b", fg: "#ffffff" };
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    { field: "routeCode", headerName: "Route Code", minWidth: 140, sortable: true },
    { field: "origin", headerName: "Origin", minWidth: 220, sortable: true },
    { field: "destination", headerName: "Destination", minWidth: 220, sortable: true },
    { field: "duration", headerName: "Duration (min)", minWidth: 130, sortable: true },
    {
      field: "active",
      headerName: "Status",
      minWidth: 120,
      sortable: true,
      renderCell: (params) => {
        const tone = getStatusBadgeStyle(params?.value);
        return <span style={{ background: tone.bg, color: tone.fg, borderRadius: 999, padding: "5px 10px", fontSize: 12, fontWeight: 700, textTransform: "uppercase", lineHeight: "16px" }}>{String(params?.value || "-")}</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleEdit(params?.row?.raw)}>
            <Edit fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}>
        <Button variant="contained" onClick={handleAdd}>Add Bus Route</Button>
      </Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => {
              const p = parseInt(newPage, 10) + 1;
              if (p >= 1) setPage(p);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
      <BusRouteCreateModal openDialog={addBusRouteModal} handleClose={handleCloseAdd} routeId={selectedRouteId} />
      <BusRouteDeleteModal openDialog={deleteBusRouteModal} handleClose={handleCloseDelete} routeId={selectedRouteId} />
    </Grid>
  );
};

export default BusRoutes;
